/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import { Ims } from '@3di/web-cloud-frontend-commons';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { ADOBE_IMS_CONFIG } from '../config';
// import { IS_PRODUCTION } from '../env-config';
import CustomError from '../CustomError';
import { USER } from '../graphql/user';
import apolloClient from '../services/apollo-client';

// const GROUP_USER = 'user';
const GROUP_CORP = 'corp';
const GROUP_ADMIN = 'admin';

class AuthService {
  _accessToken = null;
  _user = null;
  _ims = null;
  _imsLoaded = null;

  constructor() {
    this._ims = new Ims({
      ...ADOBE_IMS_CONFIG,
      onAccessTokenHasExpired: this.clearSession,
    });
  }

  login = async () => {
    this._ims.signIn();
  };

  logout = async () => {
    this._ims.signOut();
    this.clearSession();
  };

  isAuthenticated = async () => {
    try {
      await this._ims.refreshToken();
    } catch (e) {
      return false;
    }
    return !_isEmpty(this._ims.accessToken);
  };

  // Only corp users are authorized to login in all envs
  isUser = () => {
    return this.getRole() === GROUP_CORP;
  };

  isAdmin = () => {
    return this.getRole() === GROUP_ADMIN;
  };

  getAccessToken = async () => {
    this._accessToken = this._ims.accessToken;
    return this._accessToken;
  };

  async fetchUser() {
    let user;

    try {
      user = await apolloClient.query({
        query: USER,
      });
    } catch (e) {
      throw new CustomError('technical', e);
    }

    if (!user || user.error) {
      throw new CustomError('user', _get(user, 'error'));
    }

    this.storeSession(_get(user, 'data.user'));
  }

  storeSession = (user) => {
    this._user = {
      role: _get(user, 'role'),
      name: _get(user, 'name'),
      avatar: _get(user, 'avatarUrl'),
      id: _get(user, 'id'),
    };
  };

  restoreSession = async () => {
    await this._ims.refreshToken();
    const token = this._ims.accessToken;
    if (token && this._user === null) await this.fetchUser();
  };

  clearSession = () => {
    this._user = null;
  };

  getAvatar = () => {
    return _get(this._user, 'avatar');
  };

  getUsername = () => {
    return _get(this._user, 'name');
  };

  getId = () => {
    return _get(this._user, 'id');
  };

  getRole = () => {
    return _get(this._user, 'role');
  };

  get accessToken() {
    return this._ims.accessToken;
  }
}

const authService = new AuthService();

export default authService;
